import type { ContentfulLink, ContentfulAsset } from '@/types/contentful'

/**
 * Get image URL from Contentful asset link
 */
export const getImageUrlFromAsset = (assetLink: ContentfulLink, includes?: any): string | null => {
  if (!assetLink?.sys?.id || !includes?.Asset) return null

  const asset = includes.Asset.find((a: ContentfulAsset) => a.sys.id === assetLink.sys.id)
  if (!asset?.fields?.file?.url) return null

  return fixImageUrlProtocol(asset.fields.file.url)
}

/**
 * Get image alt text from Contentful asset link
 */
export const getImageAltFromAsset = (assetLink: ContentfulLink, includes?: any): string => {
  if (!assetLink?.sys?.id || !includes?.Asset) return ''

  const asset = includes.Asset.find((a: ContentfulAsset) => a.sys.id === assetLink.sys.id)
  return asset?.fields?.description || asset?.fields?.title || ''
}

/**
 * Fix image URL protocol - ensures URLs starting with // get https:// prefix
 */
export const fixImageUrlProtocol = (imageUrl: string | undefined): string => {
  if (!imageUrl) return ''
  imageUrl = imageUrl.startsWith('//') ? `https:${imageUrl}` : imageUrl
  imageUrl = imageUrl.startsWith('http:') ? imageUrl.replace('http:', 'https:') : imageUrl
  return imageUrl
}

/**
 * Determine if the Contentful imageFormat toggle is enabled
 * Supports boolean, array of strings (e.g., ['on']), or missing values
 */
export const isImageFormatOn = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value
  if (Array.isArray(value)) {
    return value.some((v) => String(v).toLowerCase() === 'on' || String(v).toLowerCase() === 'true')
  }
  if (typeof value === 'string') {
    const normalized = value.toLowerCase()
    return normalized === 'on' || normalized === 'true'
  }
  return false
}

/**
 * Standard error handling for article pages
 */
export const handleArticleNotFound = () => {
  throw createError({
    status: 404,
    statusText: 'Article not found',
  })
}

/**
 * Universal function to fetch article with all slug variations
 * Handles: exact match, with/without leading slash, trimmed versions, and fallbacks
 */
export const fetchArticleWithAllVariations = async (
  slug: string,
  fullPath: string | null = null,
  cacheKey: string,
) => {
  const { getEntries } = useContentful()

  // Use useAsyncData directly instead of useStatic to avoid throwing
  // createError(404) inside the async callback. Throwing createError
  // inside useAsyncData causes unhandled rejections during prerendering.
  // Instead, return null and throw 404 after the await.
  const { data, error } = await useAsyncData(cacheKey, async () => {
    // Build all possible slug variations to try
    const variations = [slug, `/${slug}`, slug.trim(), `/${slug.trim()}`]

    if (fullPath) {
      variations.push(fullPath, `/${fullPath}`, fullPath.trim(), `/${fullPath.trim()}`)
    }

    // Try each variation until we find a match
    for (const variation of variations) {
      try {
        const response = await getEntries({
          content_type: 'article',
          'fields.slug': variation,
          limit: 1,
          include: 2,
        })

        if (response.items.length > 0) {
          const article = response.items[0] as any
          article.includes = response.includes
          return article
        }
      } catch (error) {
        continue
      }
    }

    return null
  }, { server: true })

  if (error.value) throw error.value
  if (!data.value) handleArticleNotFound()
  return data.value
}

/**
 * Fetch article with standard error handling (legacy function)
 */
export const fetchArticleWithErrorHandling = async (slug: string, cacheKey: string) => {
  const { getArticleBySlug } = useContentful()

  const { data, error } = await useAsyncData(cacheKey, async () => {
    return await getArticleBySlug(slug)
  }, { server: true })

  if (error.value) throw error.value
  if (!data.value) handleArticleNotFound()
  return data.value
}
